import { Outlet, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useMemo } from 'react';

import {
  BookingProvider,
  ConfirmStep,
  JourneyProvider,
  LandingPage,
  MyBookingView,
  MyBookingsPage,
  PublicBookingView,
  ServicesStep,
  SlotStep,
  TechnicianStep,
  VerifyStep,
  createBookingApi,
} from '@/features/booking';
import type { BookingDeps } from '@/features/booking';
import type { BookingExtrasDeps } from '@/features/booking-extras';
import { BookingExtrasProvider, createBookingExtrasApi, nowDate } from '@/features/booking-extras';
import { randomIdempotencyKey, tenantDate } from '@/features/scheduling';

import { clientFetcher } from './bp03';
import { CustomerSignIn } from './bp11';
import { useClientDeps } from './deps';

/**
 * The BP-04 §6 client-app rows: the landing page, the five `/book/*` steps,
 * `/booking/{ref}` and `/me/bookings`.
 *
 * None of them is gated in the browser. The journey is anonymous until the OTP step
 * (`/auth/client/otp/*` are the only public write routes in §5), and everything after it
 * is scoped server-side to the cookie that verify set — a client may read and change their
 * own bookings and nothing else (R14). A gate here could only ever be decoration.
 *
 * `/booking/{ref}` has two ways in, and they show different things on purpose: with a
 * `?token=` it is the signed link, and `GET /public/bookings/{ref}` answers with an
 * appointment rather than a client record; without one it is the signed-in client's own
 * booking, which is the only view that can offer R6's reschedule and cancel.
 */

/** The tenant timezone the client app assumes until BP-01's `/me` says otherwise. */
export const CLIENT_TIMEZONE = 'Asia/Kolkata';

/** The path BP-03 serves the DPDP notice at, for the link beside the consent tick. */
export const NOTICE_PATH = '/privacy';

/** The booking feature's dependencies for the client app. */
export function useBookingFeatureDeps(): BookingDeps {
  const deps = useClientDeps();
  const fetcher = clientFetcher(deps.fetcher);
  return useMemo<BookingDeps>(
    () => ({
      api: createBookingApi(fetcher),
      newIdempotencyKey: randomIdempotencyKey,
      timezone: CLIENT_TIMEZONE,
      noticeHref: NOTICE_PATH,
    }),
    [fetcher],
  );
}

/**
 * BP-25 §6's client row: the waitlist join under a day with nothing free.
 *
 * The client holds no permissions — BP-11 R4's session is a role, not a
 * matrix — so every staff capability here is false and the one she has
 * is the join. `writable` is true: a read-only tenant is BP-10's edge
 * box talking to its own clients, and the api refuses the write anyway.
 */
export function useBookingExtrasFeatureDeps(): BookingExtrasDeps {
  const deps = useClientDeps();
  const fetcher = clientFetcher(deps.fetcher);
  return useMemo<BookingExtrasDeps>(
    () => ({
      api: createBookingExtrasApi(fetcher),
      canSee: false,
      canSchedule: false,
      canBill: false,
      canForfeit: false,
      canManageSettings: false,
      writable: true,
      newIdempotencyKey: randomIdempotencyKey,
      now: nowDate,
    }),
    [fetcher],
  );
}

/**
 * Everything under `/`, `/book/*`, `/booking/*` and `/me/bookings` shares one draft and
 * one api.
 *
 * It is a **pathless layout route** rather than a wrapper inside each screen, because the
 * five `/book/*` steps are five routes and the draft has to survive the navigation between
 * them: a provider mounted per screen would empty the basket on every "next".
 */
export function BookingLayout(): JSX.Element {
  const deps = useBookingFeatureDeps();
  const extras = useBookingExtrasFeatureDeps();
  // BP-21 "book this look" arrives as `/book/services?sku=…`. Read from the
  // location rather than from router state: the try-on screen is in another
  // feature, and a query parameter is the one handover that survives a
  // reload, a share and a back button.
  const sku = new URLSearchParams(window.location.search).get('sku');
  return (
    <BookingProvider deps={deps}>
      <BookingExtrasProvider deps={extras}>
        <JourneyProvider today={tenantDate(deps.timezone)} preselectedSkuId={sku}>
          <Outlet />
        </JourneyProvider>
      </BookingExtrasProvider>
    </BookingProvider>
  );
}

/** §6 client `/` — "Landing, Book now". */
export function BookingLandingRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <LandingPage
      onStart={() => {
        void navigate({ to: '/book/services' });
      }}
      onMine={() => {
        void navigate({ to: '/me/bookings' });
      }}
      signIn={<CustomerSignIn />}
    />
  );
}

/** §6 client `/book/services`. */
export function BookServicesRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <ServicesStep
      onNext={() => {
        void navigate({ to: '/book/technician' });
      }}
    />
  );
}

/** §6 client `/book/technician`. */
export function BookTechnicianRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <TechnicianStep
      onNext={() => {
        void navigate({ to: '/book/slot' });
      }}
    />
  );
}

/** §6 client `/book/slot`. */
export function BookSlotRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <SlotStep
      onNext={() => {
        void navigate({ to: '/book/verify' });
      }}
    />
  );
}

/** §6 client `/book/verify`. */
export function BookVerifyRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <VerifyStep
      onVerified={() => {
        void navigate({ to: '/book/confirm' });
      }}
    />
  );
}

/** §14's client cut-off, shown on the confirm step until the api says otherwise. */
export const DEFAULT_CUTOFF_HOURS = 4;

/** §6 client `/book/confirm`. */
export function BookConfirmRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <ConfirmStep
      cutoffHours={DEFAULT_CUTOFF_HOURS}
      onBooked={(booking) => {
        void navigate({ to: '/booking/$bookingRef', params: { bookingRef: booking.id } });
      }}
    />
  );
}

/** The `?token=` of a signed link, or `''`. */
export function tokenOf(search: { token?: string }): string {
  return search.token ?? '';
}

/** The `{bookingRef}` of `/booking/{ref}`, or `''` on a path without one. */
export function referenceOf(params: { bookingRef?: string }): string {
  return params.bookingRef ?? '';
}

/** §6 client `/booking/{ref}`. */
export function BookingDetailRoute(): JSX.Element {
  const params: { bookingRef?: string } = useParams({ strict: false });
  const search: { token?: string } = useSearch({ strict: false });
  const reference = referenceOf(params);
  const token = tokenOf(search);

  if (token === '') {
    return <MyBookingView bookingId={reference} />;
  }
  return <PublicBookingView reference={reference} token={token} />;
}

/** §6 client `/me/bookings`. */
export function MyBookingsRoute(): JSX.Element {
  const navigate = useNavigate();
  return (
    <MyBookingsPage
      onOpen={(id) => {
        void navigate({ to: '/booking/$bookingRef', params: { bookingRef: id } });
      }}
    />
  );
}
